import { getCurrentScope, captureEvent, captureException } from '@sentry/nextjs';
import type { SeverityLevel } from '@sentry/core';
import { logger } from '@libs/core';
import invariant from 'tiny-invariant';
import type { SentryContextType } from '../constants';

export type ErrorSeverity = 'LOW' | 'MEDIUM' | 'HIGH';

export interface ErrorLoggerOptions {
    component: string;
    severity: ErrorSeverity;
    additionalInfo?: string;
    sentryContext?: SentryContextType | null;
    tags?: Record<string, string>;
    extra?: Record<string, string>;
}

export type LogSentryErrorOptions = {
    sentryContext?: SentryContextType | null;
    tags?: {
        page?: string;
        component?: string;
        action?: string;
        severity?: string;
        [key: string]: string | undefined;
    };
    extra?: Record<string, string>;
    level?: SeverityLevel;
};

const defaultOptions: Partial<ErrorLoggerOptions> = {
    severity: 'MEDIUM',
    additionalInfo: '',
};

const scopeData = getCurrentScope()?.getScopeData();
const appName = String(scopeData?.tags?.application_name ?? 'UNKNOWN_APP');

export const logSentryEvent = (message: string, options?: LogSentryErrorOptions): void => {
    const page = options?.sentryContext?.page ?? 'UNKNOWN_PAGE';
    const action = options?.sentryContext?.action ?? 'UNKNOWN_ACTION';
    const eventMessage = `[${appName} / ${page} / ${action}]: ${message}`;

    captureEvent({
        message: eventMessage,
        tags: { action, page, ...options?.tags },
        extra: options?.extra,
        level: options?.level ?? 'info',
    });

    logger.info(eventMessage);
};

export const logSentryError = (err?: unknown, options?: LogSentryErrorOptions): void => {
    const errors = Array.isArray(err) ? err : [err ?? 'UNKNOWN_ERROR'];
    const page = options?.sentryContext?.page ?? 'UNKNOWN_PAGE';
    const action = options?.sentryContext?.action ?? 'UNKNOWN_ACTION';
    const component = options?.tags?.component ?? 'UNKNOWN_COMPONENT';
    const severity = options?.tags?.severity ?? 'MEDIUM';

    errors.forEach(error => {
        let errorMessage: string;

        if (error instanceof Error) {
            errorMessage = error.message;
        } else if (typeof error === 'string' && error !== '') {
            errorMessage = error;
        } else if (typeof error === 'object' && error !== null && error !== undefined) {
            errorMessage = JSON.stringify(error);
        } else {
            errorMessage = 'UNKNOWN_ERROR';
        }

        const formattedMessage = `[Component: ${component}] [Severity: ${severity}] ${errorMessage}`;

        const updatedError = error instanceof Error ? error : new Error(errorMessage);

        captureException(updatedError, {
            tags: { action, page, component, severity, ...options?.tags },
            extra: options?.extra,
            level: options?.level ?? 'error',
        });

        logger.error(formattedMessage);
    });
};

/**
 * Logs an error to both console and Sentry using a consistent format (winston integration)
 * Format: [Component: ComponentName] [Severity: Severity] Error message - Additional helpful context if available
 *
 * @param message - The error message
 * @param options - Options for error logging
 */
export const logConsoleError = (
    message: string,
    options: Pick<ErrorLoggerOptions, 'component' | 'severity' | 'additionalInfo' | 'extra'>
): void => {
    const { component, severity, additionalInfo, extra } = {
        ...defaultOptions,
        ...options,
    };

    invariant(component, 'Component name is required for error logging');

    const formattedMessage = `[Component: ${component}] [Severity: ${severity}] ${message}${
        additionalInfo ? ` - ${additionalInfo}` : ''
    }`;

    logger.error(formattedMessage, extra || {});
};
